import { Box, Chip, Tooltip, Typography } from "@mui/material";
import { DataGrid, GridColDef, GridPaginationModel, GridSortModel } from "@mui/x-data-grid";
import { borderRadius, colors, elevation } from "../../theme";
import { DuplicateAuditListItem } from "../../types/duplicateAudit";
import {
  displayValue,
  formatAuditDate,
  formatAuditDateTime,
  formatScore,
  joinName,
} from "../../utils/duplicateAudit";
import RiskBadge from "./RiskBadge";
import ValueText from "./ValueText";

interface Props {
  rows: DuplicateAuditListItem[];
  totalRows: number;
  loading: boolean;
  paginationModel: GridPaginationModel;
  onPaginationModelChange: (model: GridPaginationModel) => void;
  sortModel: GridSortModel;
  onSortModelChange: (model: GridSortModel) => void;
  selectedId: number | null;
  onSelect: (row: DuplicateAuditListItem) => void;
}

const personCell = (name: string, id: number | null) => (
  <Box sx={{ py: 0.5, lineHeight: 1.3 }}>
    <Typography variant="body2">
      <ValueText value={name} />
    </Typography>
    <Typography variant="caption" color={colors.text.secondary}>
      ID <ValueText value={displayValue(id)} />
    </Typography>
  </Box>
);

const columns: GridColDef<DuplicateAuditListItem>[] = [
  {
    field: "createdAtUtc",
    headerName: "Created",
    width: 160,
    renderCell: (p) => (
      <Tooltip title={formatAuditDateTime(p.row.createdAtUtc)}>
        <span>{formatAuditDate(p.row.createdAtUtc)}</span>
      </Tooltip>
    ),
  },
  {
    field: "risk",
    headerName: "Risk",
    width: 130,
    renderCell: (p) => <RiskBadge score={p.row.score} />,
  },
  { field: "createdBy", headerName: "Created by", flex: 1, minWidth: 200 },
  {
    field: "score",
    headerName: "Score",
    width: 90,
    renderCell: (p) => <strong>{formatScore(p.row.score)}</strong>,
  },
  {
    field: "newCustomer",
    headerName: "New customer",
    flex: 1,
    minWidth: 180,
    renderCell: (p) =>
      personCell(
        joinName(p.row.customerFirstName, p.row.customerLastName),
        p.row.customerSingleCustomerId
      ),
  },
  {
    field: "match",
    headerName: "Potential match",
    flex: 1,
    minWidth: 180,
    renderCell: (p) =>
      personCell(
        joinName(p.row.matchingCustomerFirstName, p.row.matchingCustomerLastName),
        p.row.matchingCustomerSingleCustomerId
      ),
  },
  {
    field: "isReviewed",
    headerName: "Status",
    width: 120,
    renderCell: (p) => (
      <Chip
        size="small"
        label={p.row.isReviewed ? "Reviewed" : "Pending"}
        sx={{
          color: "common.white",
          fontWeight: 700,
          bgcolor: p.row.isReviewed ? colors.mui.success : "grey.600",
        }}
      />
    ),
  },
];

export default function AuditQueueGrid({
  rows,
  totalRows,
  loading,
  paginationModel,
  onPaginationModelChange,
  sortModel,
  onSortModelChange,
  selectedId,
  onSelect,
}: Props) {
  return (
    <Box
      sx={{
        width: "100%",
        boxShadow: elevation.low,
        borderRadius: borderRadius.mui.medium,
        background: colors.background.sidebarGlass,
      }}
    >
      <DataGrid
        rows={rows}
        columns={columns}
        getRowId={(row) => row.auditId}
        getRowHeight={() => "auto"}
        paginationMode="server"
        sortingMode="server"
        sortingOrder={["asc", "desc"]}
        sortModel={sortModel}
        onSortModelChange={onSortModelChange}
        disableColumnMenu
        disableRowSelectionOnClick
        pageSizeOptions={[10, 20, 50, 100]}
        paginationModel={paginationModel}
        rowCount={totalRows}
        loading={loading}
        onPaginationModelChange={onPaginationModelChange}
        onRowClick={(params) => onSelect(params.row)}
        getRowClassName={(p) => (p.id === selectedId ? "audit-row-selected" : "")}
        localeText={{ noRowsLabel: "No cases match the selected filters." }}
        sx={{
          border: 0,
          minHeight: 300,
          "& .MuiDataGrid-row": {
            cursor: "pointer",
            "&:hover": { backgroundColor: colors.interaction.hover },
          },
          "& .audit-row-selected": { backgroundColor: "rgba(0, 71, 187, 0.08)" },
          "& .MuiDataGrid-cell": { display: "flex", alignItems: "center" },
        }}
      />
    </Box>
  );
}
